/**
 * ContinueWhereLeftOffCard Component
 * Unified card combining drafts, conversations, and documents
 * Shows most recent activities with single CTA per item
 */

import React from 'react';
import Card from '../common/Card';
import Button from '../common/Button';
import {
  FaFileAlt,
  FaComments,
  FaCheckCircle,
  FaPlus,
  FaArrowRight
} from 'react-icons/fa';

const ContinueWhereLeftOffCard = ({ activities, onActivityClick, onNewAction }) => {
  // Get icon based on activity type
  const getActivityIcon = (type) => {
    switch (type) {
      case 'draft':
        return FaFileAlt;
      case 'conversation':
        return FaComments;
      case 'document':
        return FaCheckCircle;
      default:
        return FaFileAlt;
    }
  };

  // Get color scheme based on activity type using design system tokens
  const getActivityColor = (type) => {
    switch (type) {
      case 'draft':
        return 'text-info-600 bg-info-50';
      case 'conversation':
        return 'text-primary-600 bg-primary-50';
      case 'document':
        return 'text-sage-600 bg-sage-50';
      default:
        return 'text-neutral-600 bg-neutral-50';
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Akkurat nå';
    if (diffMins < 60) return `${diffMins} min siden`;
    if (diffHours < 24) return `${diffHours} timer siden`;
    if (diffDays === 1) return 'I går';
    if (diffDays < 7) return `${diffDays} dager siden`;

    return date.toLocaleDateString('nb-NO', {
      day: 'numeric',
      month: 'short'
    });
  };

  return (
    <Card variant="white" padding="lg" className="mb-6">
      <div className="space-y-4">
        {/* Header */}
        <div>
          <h2 className="font-display text-xl font-semibold text-text-primary">
            Fortsett der du slapp
          </h2>
          <p className="text-sm text-text-secondary mt-1">
            Dine siste aktiviteter
          </p>
        </div>

        {/* Activities list */}
        {activities && activities.length > 0 ? (
          <div className="space-y-3">
            {activities.map((activity, index) => {
              const Icon = getActivityIcon(activity.type);
              const colorClass = getActivityColor(activity.type);

              return (
                <div
                  key={activity.id}
                  className="flex items-start gap-4 p-3 rounded-lg border border-border hover:border-primary-400 hover:bg-primary-50 transition-all duration-200 ease-smooth cursor-pointer group animate-fade-in-up"
                  style={{ animationDelay: `${index * 50}ms` }}
                  onClick={() => onActivityClick(activity)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onActivityClick(activity);
                    }
                  }}
                >
                  {/* Icon */}
                  <div className={`flex-shrink-0 w-10 h-10 rounded-lg ${colorClass} flex items-center justify-center transition-colors duration-200`}>
                    <Icon className="w-4 h-4" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary truncate group-hover:text-primary-800">
                      {activity.title}
                    </p>
                    <p className="text-xs text-text-secondary truncate mt-0.5">
                      {activity.subtitle}
                    </p>
                    <p className="text-xs text-text-muted mt-1">
                      {formatTimestamp(activity.timestamp)}
                    </p>
                  </div>

                  {/* Action indicator */}
                  <div className="flex-shrink-0">
                    <FaArrowRight className="w-3.5 h-3.5 text-neutral-400 group-hover:text-primary-600 transition-colors duration-200" />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          // Empty state — no button, just an encouraging message
          <div className="text-center py-8">
            <FaComments className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
            <p className="text-sm text-text-secondary">
              Her dukker samtalene dine opp etter hvert
            </p>
          </div>
        )}

        {/* Start new action */}
        {activities && activities.length > 0 && (
          <div className="pt-3 border-t border-border-light">
            <Button
              variant="ghost"
              size="md"
              onClick={() => onNewAction('new_conversation')}
              icon={<FaPlus className="w-4 h-4" />}
            >
              Start ny samtale
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};

export default ContinueWhereLeftOffCard;
